import { useEffect } from "react";

export interface SeoProps {
    title: string;
    description: string;
    canonicalPath: string;
    ogImage?: string;
    ogType?: string;
    jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const SITE_URL = "https://in.nuqiwealth.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/logo.png`;

function upsertMeta(attr: "name" | "property", key: string, content: string) {
    let el = document.head.querySelector<HTMLMetaElement>(
        `meta[${attr}="${key}"][data-seo-managed]`
    );
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        el.setAttribute("data-seo-managed", "true");
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function upsertCanonical(href: string) {
    let el = document.head.querySelector<HTMLLinkElement>(
        'link[rel="canonical"][data-seo-managed]'
    );
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        el.setAttribute("data-seo-managed", "true");
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

function upsertJsonLd(jsonLd: SeoProps["jsonLd"]) {
    const existing = document.head.querySelector(
        'script[type="application/ld+json"][data-seo-managed]'
    );
    if (existing) existing.remove();

    if (!jsonLd) return;

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo-managed", "true");
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);
}

export function Seo({
    title,
    description,
    canonicalPath,
    ogImage = DEFAULT_OG_IMAGE,
    ogType = "website",
    jsonLd,
}: SeoProps) {
    useEffect(() => {
        document.title = title;

        const canonicalUrl = `${SITE_URL}${canonicalPath}`;

        upsertMeta("name", "description", description);
        upsertMeta("property", "og:title", title);
        upsertMeta("property", "og:description", description);
        upsertMeta("property", "og:image", ogImage);
        upsertMeta("property", "og:type", ogType);
        upsertMeta("property", "og:url", canonicalUrl);
        upsertCanonical(canonicalUrl);
        upsertJsonLd(jsonLd);
    }, [title, description, canonicalPath, ogImage, ogType, jsonLd]);

    return null;
}
